import throttle from 'lodash/throttle';
import * as React from 'react';
import * as TypeStyle from 'typestyle';

import { Region } from '../PublicTypes';
import { ToolbarButtonLabelClass } from '../components/Button';
import { ToolbarItemClass, ToolbarOverflowItemClass } from '../components/GenericToolbar';
import { hiddenLabelStyles } from '../styles/ButtonStyles';
import { GenericToolbarItem } from '../types/GenericToolbarItem';

const AllFullWidthStyleName = TypeStyle.style({
  $nest: {
    [`.${ToolbarOverflowItemClass}`]: { display: 'none' },
  },
});

const AllHiddenLabelsStyleName = TypeStyle.style({
  $nest: {
    [`.${ToolbarOverflowItemClass}`]: {
      display: 'none',
    },
    [`.${ToolbarButtonLabelClass}`]: {
      ...hiddenLabelStyles,
    },
  },
});

export interface VizViewerToolbarRenderingBreakpointsV2 {
  /** minimum width needed to render all toolbar items as normal */
  allFullWidth: number;
  /** minimum width needed to render all toolbar items without labels */
  allHiddenLabels: number;
  /** minimum width needed to render items in the regions we always show without labels */
  alwaysShow: number;
}

/**
 * Calculates breakpoints based on measurements of toolbar items' width
 */
const useVizViewerToolbarRenderingBreakpointsV2 = (
  toolbarRef: React.RefObject<HTMLDivElement>,
  toolbarItems: GenericToolbarItem[],
  alwaysShowRegions: Region[]
): VizViewerToolbarRenderingBreakpointsV2 => {
  const [breakpoints, setBreakpoints] = React.useState<VizViewerToolbarRenderingBreakpointsV2>({
    allFullWidth: 0,
    allHiddenLabels: 0,
    alwaysShow: 0,
  });

  // breakpoints would only change if labels have changed, so merging all labels to one string to be used as a dependency
  const toolbarItemsLabelText = toolbarItems.reduce((sum, item) => sum + (item.label ?? ''), '');

  React.useLayoutEffect(() => {
    if (toolbarRef.current && toolbarItemsLabelText) {
      // Normally the classes computed via typestyle are flushed to the DOM after React renders,
      // so when we need to modify the DOM and take measurements before rendering, those measurements
      // would be incorrect as they do not take into account typestyle classes.
      // Calling forceRenderStyles is a workaround that allows typestyle classes to be flushed right away.
      //
      // Note that if we ever use shared widgets (e.g. ButtonWidget) instead of our own button
      // to render toolbar items, then we need to import forceRenderStyles from @tableau/widgets
      // and call that as well to guarantee correct measurements.
      TypeStyle.forceRenderStyles();

      const reactRenderedClassName = toolbarRef.current.className; // hack: save styles to be applied after sizing calc

      // can all the buttons fit?
      toolbarRef.current.className = AllFullWidthStyleName;
      const toolbarItemElements = Array.from(toolbarRef.current.querySelectorAll<HTMLElement>(`.${ToolbarItemClass}`));
      const allFullWidth = getWidthSum(toolbarItemElements);

      // can all the buttons fit if all the text is hidden?
      toolbarRef.current.className = AllHiddenLabelsStyleName;
      const allHiddenLabels = getWidthSum(toolbarItemElements);
      const alwaysShowItemElements = toolbarItemElements.filter(
        (item) => !!alwaysShowRegions.find((value) => item.classList.contains(value.toString()))
      );
      const alwaysShow = getWidthSum(alwaysShowItemElements);
      toolbarRef.current.className = reactRenderedClassName;

      setBreakpoints({ allFullWidth, allHiddenLabels, alwaysShow });
    }
  }, [toolbarItemsLabelText, toolbarRef, alwaysShowRegions]);

  return breakpoints;
};

const getWidthSum = (items: HTMLElement[]): number => items.reduce((sum, item) => sum + item.clientWidth, 0);

/**
 * Returns current width of the window's layout viewport (works correctly inside an iframe)
 */
const useWindowWidth = (): number => {
  const [windowWidth, setWindowWidth] = React.useState<number>(window.innerWidth);

  React.useEffect(() => {
    const handleWindowResize = throttle(() => setWindowWidth(window.innerWidth), 100);
    window.addEventListener('resize', handleWindowResize);
    return (): void => window.removeEventListener('resize', handleWindowResize);
  }, []);

  return windowWidth;
};

/**
 * Modifies `isHidden` property of `leftItems` and `rightItems` based on screen sizing.
 * All toolbar items are shown if possible. If all items with labels cannot fit, then we hide labels.
 * If all items without labels cannot fit, then we hide all regions except the regions defined in alwaysShowRegions.
 * If the regions defined in alwaysShowRegions still cannot fit, then we allow the overflow dropdown.
 *
 * @returns a boolean indicating whether the button labels should all be hidden
 */
export const useFitScreenLayoutV2 = (
  leftItems: GenericToolbarItem[],
  rightItems: GenericToolbarItem[],
  overflowRegions: Region[],
  alwaysShowRegions: Region[],
  toolbarLeftRightPadding: number,
  toolbarRef: React.RefObject<HTMLDivElement>
): [GenericToolbarItem[], GenericToolbarItem[], boolean] => {
  const windowWidth = useWindowWidth();
  const availableWidth = windowWidth - toolbarLeftRightPadding * 2;
  const breakpoints = useVizViewerToolbarRenderingBreakpointsV2(toolbarRef, leftItems.concat(rightItems), alwaysShowRegions);

  let hideButtonFilterFn: (item: GenericToolbarItem) => boolean;
  let hideLabels = false;
  if (toolbarRef.current !== null) {
    if (availableWidth > breakpoints.allFullWidth) {
      hideButtonFilterFn = (item): boolean => !!item.isOverflow;
    } else if (availableWidth > breakpoints.allHiddenLabels) {
      hideButtonFilterFn = (item): boolean => !!item.isOverflow;
      hideLabels = true;
    } else if (availableWidth > breakpoints.alwaysShow) {
      hideButtonFilterFn = (item): boolean => (item.region ? !alwaysShowRegions.includes(item.region) : true);
      hideLabels = true;
    } else {
      hideButtonFilterFn = (item): boolean =>
        !item.isOverflow && (item.region ? !alwaysShowRegions.includes(item.region) || overflowRegions.includes(item.region) : true);
      hideLabels = true;
    }
    return [...setHiddenItems(leftItems, rightItems, hideButtonFilterFn), hideLabels];
  }
  return [leftItems, rightItems, hideLabels];
};

const setHiddenItems = (
  leftItems: GenericToolbarItem[],
  rightItems: GenericToolbarItem[],
  hideButtonFilterFn: (item: GenericToolbarItem) => boolean
): [GenericToolbarItem[], GenericToolbarItem[]] => {
  const leftItemsWithHidden = leftItems.map((item) => {
    return {
      ...item,
      isHidden: hideButtonFilterFn(item),
    };
  });

  const rightItemsWithHidden = rightItems.map((item) => {
    return {
      ...item,
      isHidden: hideButtonFilterFn(item),
    };
  });

  return [leftItemsWithHidden, rightItemsWithHidden];
};
